import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import { format } from 'date-fns';
import couponService from '../../services/couponService';
import { CouponUsage } from '../../types/coupon';

interface CouponUsageDialogProps {
  open: boolean;
  onClose: () => void;
  couponId: string;
}

const CouponUsageDialog: React.FC<CouponUsageDialogProps> = ({
  open,
  onClose,
  couponId
}) => {
  const [usage, setUsage] = useState<CouponUsage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && couponId) {
      loadUsage();
    }
  }, [open, couponId]);

  const loadUsage = async () => {
    try {
      setLoading(true);
      const usageData = await couponService.getCouponUsage(couponId);
      setUsage(usageData);
    } catch (error) {
      console.error('Error loading coupon usage:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Coupon Usage History</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : usage.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              This coupon hasn't been used yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell align="right">Original Amount</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Final Amount</TableCell>
                  <TableCell>Used At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usage.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {record.orderId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {record.userId}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        ₹{record.originalAmount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`-₹${record.discountAmount.toLocaleString()}`}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        ₹{record.finalAmount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(record.usedAt), 'dd MMM yyyy, hh:mm a')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CouponUsageDialog;